import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown } from 'react-icons/fi';
import { getLocaleFromPathname } from '../config/localeRoutes';
import { buildNavPageSections, getNavStaticPages } from '../config/navPages';
import './NavPagesMenu.css';

function NavPagesMenu({ onNavigate }) {
  const { t } = useTranslation();
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const menuRef = useRef(null);

  const sections = useMemo(() => buildNavPageSections(locale), [locale]);
  const staticPages = useMemo(() => getNavStaticPages(locale), [locale]);

  const isActive = useMemo(() => {
    const path = location.pathname;
    if (staticPages.some((page) => path.startsWith(page.path))) return true;
    return sections.some((section) =>
      (section.indexPath && path === section.indexPath)
      || section.pages.some((page) => path === page.path || path.startsWith(page.path)),
    );
  }, [location.pathname, sections, staticPages]);

  const handleLinkClick = () => {
    setIsOpen(false);
    setExpandedSection(null);
    onNavigate?.();
  };

  const toggleSection = (key) => {
    setExpandedSection((current) => (current === key ? null : key));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setExpandedSection(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setExpandedSection(null);
  }, [location.pathname]);

  return (
    <div className="nav-pages-menu" ref={menuRef}>
      <button
        type="button"
        className={`nav-pages-trigger nav-link${isActive ? ' nav-link-active' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="nav-pages-panel"
      >
        {t('nav.pages')}
        <FiChevronDown className={`nav-pages-chevron${isOpen ? ' open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="nav-pages-panel" id="nav-pages-panel" role="menu">
          <div className="nav-pages-grid">
            {sections.map((section) => (
              <div key={section.key} className="nav-pages-section">
                <div className="nav-pages-section-header">
                  <p className="nav-pages-section-title">{t(`navPages.${section.key}`)}</p>
                  {section.indexPath && (
                    <Link
                      to={section.indexPath}
                      className="nav-pages-section-index"
                      onClick={handleLinkClick}
                    >
                      {t('navPages.viewAll')}
                    </Link>
                  )}
                </div>

                <button
                  type="button"
                  className={`nav-pages-section-toggle${expandedSection === section.key ? ' open' : ''}`}
                  onClick={() => toggleSection(section.key)}
                  aria-expanded={expandedSection === section.key}
                >
                  <span>{t(`navPages.${section.key}`)}</span>
                  <FiChevronDown aria-hidden="true" />
                </button>

                <ul className={`nav-pages-links${expandedSection === section.key ? ' open' : ''}`}>
                  {section.pages.map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={page.path}
                        className={`nav-pages-link${
                          location.pathname === page.path ? ' nav-pages-link-active' : ''
                        }`}
                        onClick={handleLinkClick}
                        role="menuitem"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {staticPages.length > 0 && (
              <div className="nav-pages-section nav-pages-section-static">
                <p className="nav-pages-section-title">{t('navPages.other')}</p>
                <ul className="nav-pages-links open">
                  {staticPages.map((page) => (
                    <li key={page.path}>
                      <Link
                        to={page.path}
                        className={`nav-pages-link${
                          location.pathname.startsWith(page.path) ? ' nav-pages-link-active' : ''
                        }`}
                        onClick={handleLinkClick}
                        role="menuitem"
                      >
                        {t(page.titleKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NavPagesMenu;
